import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  apiClient,
  type ConfigDefinitionDto,
  type ConfigItemDto,
  type ListConfigsFilters,
  type UpdateSystemConfigRequestDto,
} from '@/api/client';

import type { ReencryptSecretsResultDto } from './endpoints';

/**
 * Admin-only catalog-driven configuration store (PTV2-1067). The view models
 * rename the wire DTOs' snake_case audit fields (`modified_on` →
 * `modifiedOn`) to match the FE's camelCase convention; everything else is a
 * structural pass-through (canonical-string `value`/`default`, `source`, etc.).
 */

export type ConfigType = 'STRING' | 'INT' | 'LONG' | 'BOOL' | 'DURATION' | 'ENUM' | 'URL';
export type ConfigScope = 'TENANT' | 'GLOBAL';
export type ConfigSource = 'DB' | 'ENV' | 'CODE';
export type ConfigExposure = 'ADMIN' | 'AUTHENTICATED';

/** Catalog metadata for one configurable key. */
export interface ConfigDefinitionViewModel {
  readonly key: string;
  readonly namespace: string;
  readonly type: string;
  readonly scope: string;
  readonly exposure: string;
  readonly secret: boolean;
  readonly tags: readonly string[];
  readonly description: string | null;
  readonly defaultValue: string | null;
  readonly enumValues: readonly string[] | null;
}

/** One effective config row: definition + resolved value + provenance. */
export interface ConfigItemViewModel {
  readonly key: string;
  readonly namespace: string;
  readonly type: string;
  readonly scope: string;
  readonly exposure: string;
  readonly secret: boolean;
  readonly tags: readonly string[];
  readonly description: string | null;
  readonly defaultValue: string | null;
  readonly value: string | null;
  readonly isSet: boolean;
  readonly source: string;
  readonly isOverridden: boolean;
  readonly modifiedOn: string | null;
  readonly modifiedBy: string | null;
}

export function mapConfigDefinition(dto: ConfigDefinitionDto): ConfigDefinitionViewModel {
  return {
    key: dto.key,
    namespace: dto.namespace,
    type: dto.type,
    scope: dto.scope,
    exposure: dto.exposure,
    secret: dto.secret,
    tags: dto.tags ?? [],
    description: dto.description,
    defaultValue: dto.default,
    enumValues: dto.enumValues ?? null,
  };
}

export function mapConfigItem(dto: ConfigItemDto): ConfigItemViewModel {
  return {
    key: dto.key,
    namespace: dto.namespace,
    type: dto.type,
    scope: dto.scope,
    exposure: dto.exposure,
    secret: dto.secret,
    tags: dto.tags ?? [],
    description: dto.description,
    defaultValue: dto.default,
    value: dto.value,
    isSet: dto.isSet,
    source: dto.source,
    isOverridden: dto.isOverridden,
    modifiedOn: dto.modified_on,
    modifiedBy: dto.modified_by,
  };
}

export const adminConfigsRootKey = ['admin', 'configs'] as const;
export const adminConfigCatalogQueryKey = [...adminConfigsRootKey, 'catalog'] as const;

/** Stable, serializable filter slice baked into the list query key so changes refetch. */
function normalizeFilters(filters: ListConfigsFilters): Required<ListConfigsFilters> {
  return {
    q: filters.q ?? '',
    namespace: filters.namespace ?? '',
    tag: filters.tag ?? '',
    scope: filters.scope ?? '',
    diffOnly: filters.diffOnly ?? false,
  };
}

export function adminConfigListQueryKey(
  filters: ListConfigsFilters,
): readonly [...typeof adminConfigsRootKey, 'list', Required<ListConfigsFilters>] {
  return [...adminConfigsRootKey, 'list', normalizeFilters(filters)] as const;
}

export function useConfigCatalog(): UseQueryResult<ConfigDefinitionViewModel[]> {
  return useQuery<ConfigDefinitionViewModel[]>({
    queryKey: adminConfigCatalogQueryKey,
    queryFn: async ({ signal }) => {
      const list = await apiClient.getCatalog({ signal });
      return list.map(mapConfigDefinition);
    },
  });
}

export function useConfigList(filters: ListConfigsFilters): UseQueryResult<ConfigItemViewModel[]> {
  const normalized = normalizeFilters(filters);
  return useQuery<ConfigItemViewModel[]>({
    queryKey: adminConfigListQueryKey(filters),
    queryFn: async ({ signal }) => {
      const list = await apiClient.listConfigs(
        {
          ...(normalized.q !== '' ? { q: normalized.q } : {}),
          ...(normalized.namespace !== '' ? { namespace: normalized.namespace } : {}),
          ...(normalized.tag !== '' ? { tag: normalized.tag } : {}),
          ...(normalized.scope !== '' ? { scope: normalized.scope } : {}),
          ...(normalized.diffOnly ? { diffOnly: true } : {}),
        },
        { signal },
      );
      return list.map(mapConfigItem);
    },
  });
}

export interface UpsertAdminConfigInput {
  readonly key: string;
  readonly value: string;
  readonly reason?: string;
}

export function useAdminConfigMutations(): {
  readonly upsert: UseMutationResult<ConfigItemViewModel, Error, UpsertAdminConfigInput>;
  readonly reset: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();

  // After any write, both `value` AND `source`/`isOverridden`/audit fields can
  // change, and the backend evicts its own read-through cache — so a refetch is
  // the only correct reconciliation. We invalidate every list/get/catalog key
  // under the root and refetch the active list rather than swapping the value
  // optimistically.
  const invalidateAll = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: adminConfigsRootKey });
  };

  const upsert = useMutation<ConfigItemViewModel, Error, UpsertAdminConfigInput>({
    mutationFn: async ({ key, value, reason }) => {
      const body: UpdateSystemConfigRequestDto = {
        value,
        ...(reason !== undefined && reason !== '' ? { reason } : {}),
      };
      return mapConfigItem(await apiClient.upsertConfig(key, body));
    },
    onSettled: () => {
      invalidateAll().catch(() => undefined);
    },
  });

  const reset = useMutation<undefined, Error, string>({
    mutationFn: (key) => apiClient.resetConfig(key),
    onSettled: () => {
      invalidateAll().catch(() => undefined);
    },
  });

  return { upsert, reset };
}

/**
 * Re-encrypt all stored secret values to the active encryption key (PTV2-1067 D19) — a maintenance
 * action behind `admin:MANAGE`, surfaced after an encryption-key rotation. Re-encryption bumps the
 * audit fields, so the list is invalidated on settle.
 */
export function useReencryptSecrets(): UseMutationResult<ReencryptSecretsResultDto, Error, void> {
  const queryClient = useQueryClient();
  // useMutation defaults TVariables to void (so mutate() takes no args); UseMutationResult defaults
  // it to unknown, hence the explicit void on the return annotation but not on the call.
  return useMutation<ReencryptSecretsResultDto>({
    mutationFn: () => apiClient.reencryptSecrets(),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adminConfigsRootKey }).catch(() => undefined);
    },
  });
}
